import {Tooltip} from '@mantine/core';
import {type FC, forwardRef, useEffect, useState} from 'react';
import {
  COLOR_AI_GRADIENT_END,
  COLOR_AI_GRADIENT_MIDDLE,
  COLOR_AI_GRADIENT_START,
} from '../../../../../../config/constants/colorCodes';
import {cn} from '../../../../../../lib/classname';
import {getAIGradient} from '../../../../../reportAnalytics/usecase/getAIGradient';

const GRADIENT_ID = 'ai-icon-gradient';

const GradientIcon: FC = () => (
  <svg
    width={18}
    height={18}
    viewBox="0 0 24 24"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    style={{display: 'block'}}
  >
    <defs>
      <linearGradient id={GRADIENT_ID} x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor={COLOR_AI_GRADIENT_START} />
        <stop offset="50%" stopColor={COLOR_AI_GRADIENT_MIDDLE} />
        <stop offset="100%" stopColor={COLOR_AI_GRADIENT_END} />
      </linearGradient>
    </defs>
    <path fill="none" d="M0 0h24v24H0z" />
    <path
      fill={`url(#${GRADIENT_ID})`}
      d="M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z"
    />
  </svg>
);

type Props = {
  /**
   * ステップが新規作成されたかどうか
   * trueの場合、自動的にポップオーバーを開く
   */
  isNewlyCreated: boolean;

  /**
   * このステップにポップオーバーを表示すべきかどうか
   * 複数の新規作成ステップがある場合、最新のステップのみtrueになる
   */
  shouldShowPopover: boolean;

  /**
   * AI生成処理中かどうか
   */
  isGenerating?: boolean;

  /**
   * AI生成ボタンクリック時のハンドラ
   */
  onGenerateTodos?: () => void;
};

type AiTodoGenerateButtonProps = {
  onClick: () => void;
  disabled?: boolean;
  isNewlyCreated?: boolean;
  isLarge?: boolean;
};

export const AiTodoGenerateButton = forwardRef<
  HTMLButtonElement,
  AiTodoGenerateButtonProps
>(
  (
    {onClick, disabled = false, isNewlyCreated = false, isLarge = false},
    ref,
  ) => {
    return (
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={cn('hover:opacity-60 rounded-full p-[2px]', {
          'animate-pulse': isNewlyCreated,
        })}
        style={{
          background: getAIGradient(),
        }}
      >
        <span
          className={cn(
            'text-sm rounded-full flex gap-4 items-center py-[6px] px-8 bg-white',
            {'py-8': isLarge},
          )}
        >
          <span
            style={{
              backgroundImage: getAIGradient(),
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
            className="flex gap-4 items-center"
          >
            AI ToDo自動生成
            <GradientIcon />
          </span>
        </span>
      </button>
    );
  },
);

/**
 * AI TODO自動生成ボタンとポップオーバー
 *
 * StepSectionから抽出したPopover部分を独立させたコンポーネント
 * 新規作成時に自動的にポップオーバーを表示し、AI生成機能を案内する
 */
export const AiTodoGenerateButtonWithTooltip: FC<Props> = ({
  isNewlyCreated,
  shouldShowPopover,
  isGenerating = false,
  onGenerateTodos,
}) => {
  const [popoverOpened, setPopoverOpened] = useState(
    isNewlyCreated && shouldShowPopover,
  );

  useEffect(() => {
    setPopoverOpened(isNewlyCreated && shouldShowPopover);
  }, [isNewlyCreated, shouldShowPopover]);

  const handleGenerateClick = () => {
    if (onGenerateTodos) {
      onGenerateTodos();
    }
  };

  // ハンドラが未定義の場合は表示しない
  if (!onGenerateTodos) {
    return null;
  }

  return (
    <Tooltip
      label={'体験記を元にしたToDoをAIで自動生成'}
      disabled={popoverOpened}
      withArrow
    >
      <AiTodoGenerateButton
        onClick={handleGenerateClick}
        disabled={isGenerating}
        isNewlyCreated={isNewlyCreated}
      />
    </Tooltip>
  );
};
